import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { useLenis } from "lenis/react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { WHATSAPP_QUOTE_URL } from "@/i18n";
import { serviceDetails } from "@/servicesData";

const SERVICE_SLUGS = Object.keys(serviceDetails.id);

const Navbar = () => {
  const { lang, setLang, t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const lenis = useLenis();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (id) => {
    setMenuOpen(false);
    if (location.pathname !== "/") {
      navigate(`/${id}`);
      return;
    }
    if (lenis) lenis.scrollTo(id, { offset: -70, duration: 1.2 });
    else document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const links = [
    { id: "#about", label: t.nav.about, testid: "nav-link-about" },
    { id: "#services", label: t.nav.services, testid: "nav-link-services" },
    { id: "#industries", label: t.nav.industries, testid: "nav-link-industries" },
    { id: "#gallery", label: t.nav.gallery, testid: "nav-link-gallery" },
    { id: "#contact", label: t.nav.contact, testid: "nav-link-contact" },
  ];

  const textColor = scrolled ? "text-navy" : "text-white";

  return (
    <header
      data-testid="navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,backdrop-filter] duration-500 ${
        scrolled
          ? "bg-white/85 shadow-[0_8px_32px_rgba(0,43,91,0.10)] backdrop-blur-xl"
          : "bg-gradient-to-b from-navy-950/70 to-transparent"
      }`}
    >
      <div className="mx-auto flex h-[70px] max-w-7xl items-center justify-between px-5 sm:px-8">
        <button
          data-testid="nav-logo-button"
          onClick={() => goTo("#home")}
          className="flex items-center gap-3"
          aria-label="Trinita Bahana Persada - Home"
        >
          <span className="overflow-hidden rounded-lg shadow-md ring-1 ring-navy/10">
            <img src="/assets/logo.png" alt="Logo CV Trinita Bahana Persada" className="h-11 w-11 object-cover sm:h-12 sm:w-12" />
          </span>
          <span className={`hidden flex-col leading-tight md:flex ${textColor}`}>
            <span className="font-display text-sm font-bold tracking-wide">TRINITA</span>
            <span className="text-[9px] font-semibold uppercase tracking-[0.3em] opacity-70">
              Bahana Persada
            </span>
          </span>
        </button>

        <nav className="hidden items-center gap-7 lg:flex" aria-label="Main navigation">
          {links.map((link) =>
            link.id === "#services" ? (
              <div key={link.id} className="group relative">
                <button
                  data-testid={link.testid}
                  onClick={() => goTo(link.id)}
                  className={`group/btn relative text-[13px] font-semibold tracking-wide transition-colors duration-300 ${textColor} hover:text-gold`}
                >
                  {link.label}
                  <span className="absolute -bottom-1 left-0 h-px w-0 bg-gold transition-[width] duration-300 group-hover/btn:w-full" aria-hidden="true" />
                </button>
                <div
                  data-testid="nav-services-dropdown"
                  className="invisible absolute left-1/2 top-full z-50 w-72 -translate-x-1/2 pt-4 opacity-0 transition-[opacity] duration-300 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100"
                >
                  <div className="overflow-hidden rounded-xl border border-navy/10 bg-white py-2 shadow-[0_16px_48px_rgba(0,43,91,0.15)]">
                    {t.services.items.map((service, i) => (
                      <Link
                        key={service.title}
                        data-testid={`nav-dropdown-service-${i}`}
                        to={`/services/${SERVICE_SLUGS[i]}`}
                        className="flex items-center justify-between px-5 py-2.5 text-[13px] font-semibold text-navy transition-colors duration-200 hover:bg-mist hover:text-gold-dark"
                      >
                        <span>
                          <span className="mr-2 font-display text-[10px] italic text-gold">
                            {String(i + 1).padStart(2, "0")}
                          </span>
                          {service.title}
                        </span>
                        <ArrowUpRight className="h-3.5 w-3.5 opacity-0 transition-opacity duration-200 group-hover:opacity-60" aria-hidden="true" />
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <button
                key={link.id}
                data-testid={link.testid}
                onClick={() => goTo(link.id)}
                className={`group relative text-[13px] font-semibold tracking-wide transition-colors duration-300 ${textColor} hover:text-gold`}
              >
                {link.label}
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-gold transition-[width] duration-300 group-hover:w-full" aria-hidden="true" />
              </button>
            )
          )}
        </nav>

        <div className="flex items-center gap-3">
          <div
            data-testid="language-toggle"
            className={`flex items-center rounded-full border p-0.5 text-[11px] font-bold ${
              scrolled ? "border-navy/20" : "border-white/30"
            }`}
          >
            {["id", "en"].map((l) => (
              <button
                key={l}
                data-testid={`lang-${l}`}
                onClick={() => setLang(l)}
                className={`rounded-full px-2.5 py-1 uppercase tracking-wider transition-colors duration-300 ${
                  lang === l
                    ? "bg-gold text-navy-950"
                    : scrolled
                      ? "text-navy/60 hover:text-navy"
                      : "text-white/70 hover:text-white"
                }`}
              >
                {l}
              </button>
            ))}
          </div>

          <a
            data-testid="nav-quote-button"
            href={WHATSAPP_QUOTE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group hidden items-center gap-1.5 rounded-full bg-gold px-5 py-2.5 text-[13px] font-bold text-navy-950 transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-gold-light sm:flex"
          >
            {t.nav.quote}
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" aria-hidden="true" />
          </a>

          <button
            data-testid="nav-menu-button"
            onClick={() => setMenuOpen(!menuOpen)}
            className={`lg:hidden ${textColor}`}
            aria-label="Toggle menu"
          >
            {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden bg-white/95 backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-5">
              {links.map((link) =>
                link.id === "#services" ? (
                  <div key={link.id}>
                    <button
                      data-testid={`mobile-${link.testid}`}
                      onClick={() => goTo(link.id)}
                      className="py-2.5 text-left text-sm font-semibold text-navy transition-colors duration-300 hover:text-gold"
                    >
                      {link.label}
                    </button>
                    <div className="mb-2 ml-3 flex flex-col border-l border-navy/15 pl-4">
                      {t.services.items.map((service, i) => (
                        <Link
                          key={service.title}
                          data-testid={`mobile-nav-service-${i}`}
                          to={`/services/${SERVICE_SLUGS[i]}`}
                          onClick={() => setMenuOpen(false)}
                          className="py-1.5 text-[13px] font-medium text-navy/70 transition-colors duration-300 hover:text-gold-dark"
                        >
                          {service.title}
                        </Link>
                      ))}
                    </div>
                  </div>
                ) : (
                  <button
                    key={link.id}
                    data-testid={`mobile-${link.testid}`}
                    onClick={() => goTo(link.id)}
                    className="py-2.5 text-left text-sm font-semibold text-navy transition-colors duration-300 hover:text-gold"
                  >
                    {link.label}
                  </button>
                )
              )}
              <a
                data-testid="mobile-quote-button"
                href={WHATSAPP_QUOTE_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMenuOpen(false)}
                className="mt-3 flex items-center justify-center gap-1.5 rounded-full bg-gold px-5 py-3 text-sm font-bold text-navy-950"
              >
                {t.nav.quote}
                <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
